import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SpinnerLoader } from "@activecollab/components";
import { intersection } from "lodash";
import PropTypes from "prop-types";
import { fetchReminders } from "~/store/actions/actions.reminders.jsx";
import { deleteReminder } from "~/store/actions/actions.reminders.jsx";
import { getRemindersByParent } from "~/store/selectors/selector.reminders.jsx";
import Reminders from "./Reminders.jsx";
import { RELATED_FIELD_BY_PARENT_TYPE, RELATIVE_REMINDERS_SUPPORTED_PARENT_TYPES } from "./utils.js";
import { isRepeating } from "../Calendar/calendarUtils.js";
import { useGlobalPrivateChannelListener } from "../Hooks/useGlobalPrivateChannelListener.js";

const RemindersContainer = ({ parent, members }) => {
  const dispatch = useDispatch();

  const [relatedFieldValue, setRelatedFieldValue] = useState(() => {
    if (parent && parent?.class) {
      if (parent.class === "CalendarEvent" && isRepeating(parent)) {
        return {
          starts_on: parent.starts_on,
          repeat_event: parent.repeat_event,
          repeat_until: parent.repeat_until,
          exceptions: parent.exceptions ?? [],
        };
      }
      return { [RELATED_FIELD_BY_PARENT_TYPE[parent?.class]]: parent[RELATED_FIELD_BY_PARENT_TYPE[parent?.class]] };
    }

    return null;
  });
  const { id = 0, class: parentType } = parent || {};

  useEffect(() => {
    if (id > 0) {
      dispatch(fetchReminders(id, parentType));
    }
  }, [dispatch, id, parentType]);

  const reminders = useSelector((state) => getRemindersByParent(state, parentType, id));

  const handleDelete = useCallback(
    (id) => {
      dispatch(deleteReminder(id));
    },
    [dispatch]
  );

  const checkRepeatingEvent = useCallback(
    (data) => {
      if (
        relatedFieldValue.starts_on !== data.starts_on ||
        relatedFieldValue.repeat_event !== data.repeat_event ||
        relatedFieldValue.repeat_until !== data.repeat_until
      ) {
        return true;
      } else {
        let interesected = intersection(relatedFieldValue.exceptions, data.exceptions);
        if (interesected.length !== data.exceptions.length) {
          return true;
        }
        return false;
      }
    },
    [relatedFieldValue]
  );

  const parentUpdatedHandler = useCallback(
    (data) => {
      let refetch = false;
      if (
        RELATIVE_REMINDERS_SUPPORTED_PARENT_TYPES.includes(parentType) &&
        RELATIVE_REMINDERS_SUPPORTED_PARENT_TYPES.includes(data?.class) &&
        id === data.id &&
        relatedFieldValue
      ) {
        if (parentType === "CalendarEvent" && isRepeating(parent)) {
          if (checkRepeatingEvent(data)) {
            refetch = true;
          }
        } else {
          if (
            relatedFieldValue[RELATED_FIELD_BY_PARENT_TYPE[data.class]] !==
            data[RELATED_FIELD_BY_PARENT_TYPE[data.class]]
          ) {
            refetch = true;
          }
        }
      }

      if (parentType === "CalendarEvent" && isRepeating(parent)) {
        setRelatedFieldValue({
          starts_on: data.starts_on,
          repeat_event: data.repeat_event,
          repeat_until: data.repeat_until,
          exceptions: data.exceptions,
        });
      } else {
        setRelatedFieldValue({
          [RELATED_FIELD_BY_PARENT_TYPE[data.class]]: data[RELATED_FIELD_BY_PARENT_TYPE[data.class]],
        });
      }

      if (refetch) {
        dispatch(fetchReminders(id, parentType));
      }
    },
    [checkRepeatingEvent, dispatch, id, parent, parentType, relatedFieldValue]
  );

  useGlobalPrivateChannelListener(`${parentType}Updated`, parentUpdatedHandler);

  const exceptionCreatedHandler = useCallback(
    (data) => {
      if (
        parentType === "CalendarEvent" &&
        isRepeating(parent) &&
        data.uid === parent.uid &&
        data.recurrence_instance !== null
      ) {
        dispatch(fetchReminders(id, parentType));
      }
    },
    [dispatch, id, parent, parentType]
  );

  useGlobalPrivateChannelListener(`CalendarEventCreated`, exceptionCreatedHandler);

  return reminders && id > 0 ? (
    <Reminders
      reminders={reminders}
      parent={parent}
      members={members}
      onDelete={handleDelete}
    />
  ) : (
    <div
      style={{ height: "100px" }}
      className="tw-flex tw-justify-center tw-items-center"
    >
      <SpinnerLoader data-testid="loader" />
    </div>
  );
};

RemindersContainer.propTypes = {
  parent: PropTypes.object,
  members: PropTypes.array.isRequired,
};

export default RemindersContainer;
